import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from 'react-toastify';
import { Download, Trash2, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersTab = ({ users, removeUser }) => {
  const [exporting, setExporting] = useState(false);

  const exportFromServer = async () => {
    setExporting(true);
    try {
      const link = document.createElement('a');
      link.href = `${API}/admin/export/users`;
      link.download = `luminelle_users_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Excel export started - check your downloads');
    } catch (error) {
      toast.error('Export failed');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Users ({users.length})</h2>
        <Button
          onClick={exportFromServer}
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
          disabled={users.length === 0 || exporting}
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </div>

      <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-xs font-bold text-burgundy">💡</span>
          </div>
          <div>
            <h4 className="font-semibold text-burgundy mb-1">CRM Integration Ready</h4>
            <p className="text-sm text-burgundy/70">
              Export includes all user demographics for CRM integration. You can also remove disruptive users.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-xl border border-burgundy/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[15%]" />
              <col className="w-[20%]" />
              <col className="w-[8%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[11%]" />
            </colgroup>
            <thead className="bg-burgundy text-cream">
              <tr>
                <th className="px-6 py-4 text-left font-semibold">Name</th>
                <th className="px-6 py-4 text-left font-semibold">Email</th>
                <th className="px-6 py-4 text-left font-semibold">Age</th>
                <th className="px-6 py-4 text-left font-semibold">Location</th>
                <th className="px-6 py-4 text-left font-semibold">Membership</th>
                <th className="px-6 py-4 text-left font-semibold">Joined Date</th>
                <th className="px-6 py-4 text-left font-semibold">Status</th>
                <th className="px-6 py-4 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id} className={index % 2 === 0 ? 'bg-white' : 'bg-cream/30'}>
                  <td className="px-6 py-4 text-burgundy font-medium">{user.name}</td>
                  <td className="px-6 py-4 text-burgundy/80 truncate" title={user.email}>{user.email}</td>
                  <td className="px-6 py-4 text-burgundy/80">{user.age || 'N/A'}</td>
                  <td className="px-6 py-4 text-burgundy/80">{user.location || 'Not provided'}</td>
                  <td className="px-6 py-4">
                    <Badge 
                      variant="outline" 
                      className={
                        user.coupon_used === 'FOUNDING48'
                          ? 'bg-gold text-burgundy border-gold'
                        : user.subscription_status === 'trial' 
                          ? 'bg-white text-burgundy border-burgundy/20' 
                        : user.subscription_status === 'canceled'
                          ? 'bg-red-100 text-red-700 border-red-300'
                          : 'bg-dark-teal text-cream border-dark-teal'
                      }
                    >
                      {user.coupon_used === 'FOUNDING48' ? 'Founding 48'
                      : user.subscription_status === 'trial' ? `Trial${user.days_remaining ? ` (${user.days_remaining}d)` : ''}`
                      : user.subscription_status === 'canceled' ? 'Canceled'
                      : 'Member'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-burgundy/80">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <Badge variant={user.is_active ? "default" : "secondary"} className="bg-gold/20 text-gold border-gold/30">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      onClick={() => removeUser(user.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="text-center py-12 text-burgundy/60">
            <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-semibold mb-2">No users found</p>
            <p className="text-sm">Users will appear here as they register</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersTab;
